import React, {useState, useMemo, useEffect} from 'react'
import moment from 'moment'
import _ from 'lodash'

import {IconButton, Popover} from '@material-ui/core'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import './Month.scss'
import PopTimePicker from './PopTimePicker'

const Month = ({
  momentDate,
  onClickNextMonth,
  onClickPrevMonth,
  onClickDay,
  onClickTime,
  hidePrevMonth,
  hideNextMonth,
  selectedFromMomentDate,
  selectedToMomentDate,
  disableDatesBefore,
  disableDatesAfter,
  dateSelection = null,
}) => {
  window.moment = moment
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [month, setMonth] = useState(moment().startOf('month'))
  const [selectedDate, setSelectedDate] = React.useState(null)
  const [times, setTimes] = useState([])

  // day of week: 0 = sunday, 6 = saturday
  const dayOfWeek = useMemo(() => month.day(), [month])
  const daysInMonth = useMemo(() => month.daysInMonth(), [month])
  const calendarDays = useMemo(
    () => [
      ...Array.from({length: dayOfWeek}),
      ...Array.from({length: daysInMonth}).map((e, i) => i + 1),
    ],
    [dayOfWeek, daysInMonth]
  )
  const daysInWeek = useMemo(() => _.chunk(calendarDays, 7), [calendarDays])
  selectedFromMomentDate = moment(selectedFromMomentDate)
  selectedToMomentDate = moment(selectedToMomentDate)
  disableDatesBefore = moment(disableDatesBefore)
  disableDatesAfter = moment(disableDatesAfter)
  const selectedFromDay = useMemo(() => {
    if (!selectedFromMomentDate) return null

    if (
      moment(selectedFromMomentDate).get('month') === month.get('month') &&
      moment(selectedFromMomentDate).get('year') === month.get('year')
    ) {
      return moment(selectedFromMomentDate).get('date')
    }
    return null
  }, [selectedFromMomentDate, month])

  const selectedToDay = useMemo(() => {
    if (!selectedToMomentDate) return null

    if (
      moment(selectedToMomentDate).get('month') === month.get('month') &&
      moment(selectedToMomentDate).get('year') === month.get('year')
    ) {
      return moment(selectedToMomentDate).get('date')
    }
    return null
  }, [selectedToMomentDate, month])

  const startHighlight = useMemo(() => {
    // start is null
    if (!selectedFromMomentDate) return null
    // current month
    if (
      moment(selectedFromMomentDate).get('month') === month.get('month') &&
      moment(selectedFromMomentDate).get('year') === month.get('year')
    ) {
      return selectedFromMomentDate.get('date')
    }
    // prev
    if (
      moment(selectedFromMomentDate).get('month') < month.get('month') &&
      moment(selectedFromMomentDate).get('year') <= month.get('year')
    ) {
      return 0
    }
    // next
    if (
      moment(selectedFromMomentDate).get('month') > month.get('month') &&
      moment(selectedFromMomentDate).get('year') >= month.get('year')
    ) {
      return null
    }

    // new condition
    if (
      moment(selectedToMomentDate).get('year') > moment(selectedFromMomentDate).get('year') &&
      moment(selectedFromMomentDate).get('year') <= month.get('year')
    ) {
      return 0
    }
  }, [selectedFromMomentDate, month, selectedToMomentDate])

  const toHighlight = useMemo(() => {
    // to is null
    if (!selectedToMomentDate) return null
    // current month
    if (
      moment(selectedToMomentDate).get('month') === month.get('month') &&
      moment(selectedToMomentDate).get('year') === month.get('year')
    ) {
      return moment(selectedToMomentDate).get('date')
    }
    // prev
    if (
      moment(selectedToMomentDate).get('month') < month.get('month') &&
      moment(selectedToMomentDate).get('year') <= month.get('year')
    ) {
      return 0
    }
    // next
    if (
      moment(selectedToMomentDate).get('month') > month.get('month') &&
      moment(selectedToMomentDate).get('year') >= month.get('year')
    ) {
      return 32
    }
    // new condition
    if (moment(selectedToMomentDate).get('year') > month.get('year')) {
      return 32
    }
  }, [selectedToMomentDate, month])

  const disableDayBefore = useMemo(() => {
    // start is null
    if (!disableDatesBefore) return null
    // current month
    if (
      moment(disableDatesBefore).get('month') === month.get('month') &&
      moment(disableDatesBefore).get('year') === month.get('year')
    ) {
      return moment(disableDatesBefore).get('date')
    }
    // prev
    if (
      moment(disableDatesBefore).get('month') < month.get('month') &&
      moment(disableDatesBefore).get('year') <= month.get('year')
    ) {
      return 0
    }
    // next
    if (
      moment(disableDatesBefore).get('month') > month.get('month') &&
      moment(disableDatesBefore).get('year') >= month.get('year')
    ) {
      return 32
    }
    if (moment(disableDatesBefore).get('year') > month.get('year')) {
      return 32
    }
  }, [disableDatesBefore, month])

  const disableDayAfter = useMemo(() => {
    // to is null
    if (!disableDatesAfter) return null
    // current month
    if (
      moment(disableDatesAfter).get('month') === month.get('month') &&
      moment(disableDatesAfter).get('year') === month.get('year')
    ) {
      return moment(disableDatesAfter).get('date')
    }
    // prev
    if (
      moment(disableDatesAfter).get('month') < month.get('month') &&
      moment(disableDatesAfter).get('year') <= month.get('year')
    ) {
      return -1
    }
    // next
    if (
      moment(disableDatesAfter).get('month') > month.get('month') &&
      moment(disableDatesAfter).get('year') >= month.get('year')
    ) {
      return 32
    }
  }, [disableDatesAfter, month])

  const handleClickNextMonth = () => {
    setMonth(p => p.add(1, 'month').clone())
  }
  const handleClickPrevMonth = () => {
    setMonth(p => p.subtract(1, 'month').clone())
  }
  const handleClickDay = (e, day) => {
    if (!day) return

    let default_date = month
      .clone()
      .set('date', day)
      .set('hour', 8)
      .set('minute', 0)
    if (dateSelection === 'from' && !!selectedFromMomentDate && selectedFromMomentDate.isValid()) {
      default_date = default_date
        .set('hour', selectedFromMomentDate.hours())
        .set('minute', selectedFromMomentDate.minutes())
    }

    if (dateSelection === 'to' && !!selectedToMomentDate && selectedToMomentDate.isValid()) {
      default_date = default_date
        .set('hour', selectedToMomentDate.hours())
        .set('minute', selectedToMomentDate.minutes())
    }

    // set times
    const _date = default_date.clone().startOf('day')
    let times = []

    for (let i = 0; i < 48; i++) {
      const _n = _date.clone().add('minutes', i * 30)
      times.push(_n)
    }
    // get default time
    const time = times.find(f => f.valueOf() > disableDatesBefore.valueOf())

    if (time && time.valueOf() > default_date.valueOf()) {
      default_date = time.clone()
    }

    if (onClickDay) {
      onClickDay(e, default_date)
    }

    setSelectedDate(default_date.clone())
    setTimes(times)
    setAnchorEl(e.target)
  }
  const handleClickTime = (e, momentDate) => {
    if (onClickTime) {
      onClickTime(e, momentDate.clone())
    }
    setAnchorEl(null)
  }

  useEffect(() => {
    setMonth(momentDate.startOf('month'))
  }, [momentDate])

  return (
    <div>
      <div className="c-calendar-month">
        <div style={{display: 'flex'}}>
          <div className="ap-flex ap-flex--start">
            {!hidePrevMonth && (
              <IconButton
                size="small"
                className="c-res-mod ap-icon-button"
                onClick={onClickPrevMonth ? onClickPrevMonth : handleClickPrevMonth}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>
            )}
          </div>
          <div className="c-calendar-month__month">{month.format('MMMM YYYY')}</div>
          <div className="ap-flex ap-flex--end">
            {!hideNextMonth && (
              <IconButton
                size="small"
                className="c-res-mod ap-icon-button"
                onClick={onClickNextMonth ? onClickNextMonth : handleClickNextMonth}
              >
                <KeyboardArrowRightIcon />
              </IconButton>
            )}
          </div>
        </div>
        <div style={{display: 'flex'}}>
          <div className="c-calendar-month__day-of-week">Sun</div>
          <div className="c-calendar-month__day-of-week">Mon</div>
          <div className="c-calendar-month__day-of-week">Tue</div>
          <div className="c-calendar-month__day-of-week">Wed</div>
          <div className="c-calendar-month__day-of-week">Thu</div>
          <div className="c-calendar-month__day-of-week">Fri</div>
          <div className="c-calendar-month__day-of-week">Sat</div>
        </div>
        <div>
          {daysInWeek.map((diw, i) => (
            <div key={i} style={{display: 'flex'}}>
              {diw.map((d, i) => {
                return (
                  <div key={i} className={`c-calendar-month__tile`}>
                    {startHighlight !== null &&
                      startHighlight !== undefined &&
                      toHighlight !== null &&
                      toHighlight !== undefined &&
                      d >= startHighlight &&
                      d <= toHighlight && (
                        <div
                          className={`c-calendar-month__day-highlight ${startHighlight === d &&
                            'c-calendar-month__day-highlight--start'} ${toHighlight === d &&
                            'c-calendar-month__day-highlight--end'}`}
                        />
                      )}
                    <div
                      onClick={e => handleClickDay(e, d)}
                      className={`c-calendar-month__day ${
                        !d ||
                        (disableDayBefore !== null && d < disableDayBefore) ||
                        (disableDayAfter !== null && d > disableDayAfter)
                          ? 'c-calendar-month__day--disabled'
                          : null
                      } ${
                        d && (d === selectedFromDay || d === selectedToDay)
                          ? 'c-calendar-month__day--selected'
                          : null
                      }`}
                    >
                      {d}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      {onClickTime && (
        <PopTimePicker
          open={!!anchorEl}
          anchorEl={anchorEl}
          date={selectedDate}
          disableDatesBefore={disableDatesBefore}
          times={times}
          onClickTime={handleClickTime}
          onClose={e => setAnchorEl(null)}
        />
      )}
    </div>
  )
}

export default Month
